/**
 * errorReporting — minimal Sentry-compatible stub for pre-launch infra.
 *
 * Phase 7 v149 — adapter layer between the app and a future Sentry SDK.
 * Today: silent unless `VITE_SENTRY_DSN` is set (we don't have a paid
 * Sentry tier yet); throws-or-logs only in dev. Once Sentry is wired,
 * swap the `report*` impls без trail of caller changes.
 *
 * Why a stub now: lets us start sprinkling `reportError(e, context)` at
 * call sites (api error boundaries, hook catches, payment flows) so when
 * the SDK arrives there's no need to grep-and-rewrite — just flip the
 * impl in this file.
 */

export interface ErrorContext {
  /** Domain area — billing / wellness / plan / chat / auth / etc. */
  area?: string;
  /** Free-form key→value for breadcrumbs (athleteId, raceId, etc). */
  extra?: Record<string, unknown>;
  /** Severity hint — affects future Sentry level mapping. */
  level?: "fatal" | "error" | "warning" | "info";
  /** Tags для filtering in the future dashboard. */
  tags?: Record<string, string>;
}

const DSN: string | undefined =
  // Vite env variables are typed; only read when defined.
  typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
    ? (import.meta.env.VITE_SENTRY_DSN as string | undefined)
    : undefined;

const ENABLED = typeof DSN === "string" && DSN.length > 0;
const IS_DEV =
  typeof import.meta !== "undefined" &&
  typeof import.meta.env !== "undefined" &&
  import.meta.env.MODE !== "production";

/**
 * reportError — primary entry for caught exceptions. Safe to call с
 * unknown types; we normalize to Error before forwarding.
 */
export function reportError(err: unknown, context: ErrorContext = {}): void {
  if (!ENABLED) {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.warn("[errorReporting:dev]", err, context);
    }
    return;
  }
  // Real Sentry SDK wiring lands when DSN is procured. Stub forwarding
  // shape mirrors Sentry.captureException(err, { extra, tags, level })
  // so the switch is one-line.
  const normalized = err instanceof Error ? err : new Error(String(err));
  // eslint-disable-next-line no-console
  console.error("[errorReporting]", normalized, context);
}

/**
 * reportMessage — for non-throw signals (degraded state, missing data
 * we'd otherwise silently fall back on). Use sparingly.
 */
export function reportMessage(
  message: string,
  context: ErrorContext = {},
): void {
  if (!ENABLED) {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.info("[errorReporting:dev]", message, context);
    }
    return;
  }
  // eslint-disable-next-line no-console
  console.warn("[errorReporting]", message, context);
}

/**
 * isReportingEnabled — UI affordance check (e.g. show "report this bug"
 * button only when a DSN is configured).
 */
export function isReportingEnabled(): boolean {
  return ENABLED;
}
